import React, { useState, useEffect } from 'react';
import { Plus, Search, Loader2, Grid, List, Wand2, X, ArrowRight, Briefcase } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { contentService } from '../../services/ContentService';
import BrandIdentitySummary from '../../components/contents/BrandIdentitySummary';
import RecentImagesGallery from '../../components/contents/RecentImagesGallery';
import ProjectFolderCard from '../../components/contents/batches/ProjectFolderCard';

const ContentBatchesList = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [scrolled, setScrolled] = useState(false);
  const [servicesBannerDismissed, setServicesBannerDismissed] = useState(
    () => localStorage.getItem('services_banner_dismissed') === 'true'
  );

  const dismissServicesBanner = () => {
    setServicesBannerDismissed(true);
    localStorage.setItem('services_banner_dismissed', 'true');
  };

  // Pegar primeiro nome do usuário
  const firstName = currentUser?.name?.split(' ')[0] || 'Usuário';

  // Buscar projetos
  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = { limit: 50, offset: 0 };
      const response = await contentService.listBatches(params);
      const projectsData = response?.data || [];
      
      const filtered = searchTerm
        ? projectsData.filter(project =>
            project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            project.user_prompt?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : projectsData;

      setProjects(Array.isArray(filtered) ? filtered : []);
    } catch (err) {
      console.error('Erro ao buscar projetos:', err);
      setError(err.response?.data?.detail || err.message || 'Não foi possível carregar os projetos.');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Busca com debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProjects();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Deletar projeto
  const handleDeleteProject = async (project) => {
    const confirmDelete = window.confirm(
      `Excluir "${project.title}"? Esta ação não pode ser desfeita.`
    );
    if (!confirmDelete) return;

    try {
      await contentService.deleteBatch(project.id);
      setProjects(prev => prev.filter(p => p.id !== project.id));
    } catch (err) {
      console.error('Erro ao excluir projeto:', err);
      alert(`Erro ao excluir: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Navegar para visualização
  const handleViewProject = (project) => {
    navigate(`/contents/batch/${project.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* ===== PAGE HEADER - Card Flutuante ===== */}
      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-6xl mx-auto">
          <div className={`bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 transition-shadow shadow-sm`}>
            <div className="flex items-center justify-between h-20">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                  <Wand2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Olá, {firstName} 👋</p>
                  <h1 className="text-lg font-bold text-gray-900">O que vamos criar hoje?</h1>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/contents/new')}
                className="hidden sm:flex px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl transition-all duration-300 items-center gap-2 shadow-md hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5"
              >
                <Plus className="w-5 h-5" />
                <span>Novo Projeto</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ===== PAGE CONTENT ===== */}
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 lg:py-6">
        
        {/* ===== BANNER: Servicos Profissionais ===== */}
        {!servicesBannerDismissed && (
          <section className="mb-6">
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-5 sm:p-6 text-white overflow-hidden">
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg mb-1">Serviços Profissionais</h3>
                  <p className="text-white/80 text-sm">
                    Identidade visual, tráfego pago, branding e planejamento estratégico. Potencialize seu negócio direto pela plataforma.
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                  <Link
                    to="/services"
                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-colors"
                  >
                    Conhecer
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={dismissServicesBanner}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="sm:hidden flex items-center gap-2 mt-4">
                <Link
                  to="/services"
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-colors"
                >
                  Conhecer Serviços
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={dismissServicesBanner}
                  className="p-2.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ===== SEÇÃO 1: Identidade Visual ===== */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Identidade Visual
            </h2>
          </div>
          <BrandIdentitySummary />
        </section>

        {/* ===== SEÇÃO 2: Galeria de Imagens Recentes ===== */}
        {!isLoading && projects.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Criações Recentes
              </h2>
            </div>
            <RecentImagesGallery projects={projects} />
          </section>
        )}

        {/* ===== SEÇÃO 3: Projetos ===== */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Meus Projetos ({projects.length})
            </h2>

            {/* Controles */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar projetos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all"
                />
              </div>

              {/* View Toggle */}
              <div className="hidden sm:flex items-center bg-white border border-gray-200 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'list' 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mb-4">
                <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
              </div>
              <p className="text-gray-500 font-medium">Carregando projetos...</p>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-red-700 font-medium mb-2">Erro ao carregar projetos</p>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button 
                onClick={fetchProjects}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {/* Projects Grid */}
          {!isLoading && projects.length > 0 && (
            <div className={`grid gap-5 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 sm:grid-cols-3 lg:grid-cols-4' 
                : 'grid-cols-1'
            }`}>
              {projects.map(project => (
                <ProjectFolderCard
                  key={project.id}
                  batch={project}
                  onClick={handleViewProject}
                  onDelete={handleDeleteProject}
                  viewMode={viewMode}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && projects.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center">
                <svg className="w-10 h-10 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {searchTerm ? 'Nenhum projeto encontrado' : 'Comece criando seu primeiro projeto'}
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                {searchTerm 
                  ? 'Tente outro termo de busca' 
                  : 'Crie conteúdos incríveis com inteligência artificial em poucos cliques'}
              </p>
              {!searchTerm && (
                <Link
                  to="/contents/new"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Plus className="w-5 h-5" />
                  Criar Novo Projeto
                </Link>
              )}
            </div>
          )}
        </section>

        {/* FAB Mobile - bottom-24 para não conflitar com widget de chat */}
        <Link
          to="/contents/new"
          className="fixed bottom-24 right-6 sm:hidden w-14 h-14 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white rounded-2xl shadow-lg hover:shadow-xl flex items-center justify-center transition-all active:scale-95 z-30"
        >
          <Plus className="w-6 h-6" />
        </Link>
      </div>
    </div>
  );
};

export default ContentBatchesList;